/**
 * The age gate's two jobs that the markup cannot do for itself: remember the answer, and refuse
 * Escape.
 *
 * Everything else is native. `snippets/age-check.liquid` renders a real <dialog> whose panel is a
 * `<form method="dialog">`, and an inline block opens it before the shop paints. So this file is
 * an enhancement, never a dependency: if it fails to load, the visitor still presses Enter, the
 * browser still closes the dialog, and the only thing lost is that they will be asked again on the
 * next page, and that the page behind can be scrolled while the gate is up. It stays inert either
 * way, because that part is `showModal()`'s doing rather than ours. That is the correct way for an
 * age gate to break.
 */

import { lockScroll, unlockScroll } from '@theme/utilities';

const KEY = 'givino:age-check';
const dialog = document.getElementById('AgeCheck');

if (dialog instanceof HTMLDialogElement) {
  /**
   * `close` fires for the native `method="dialog"` submit as well as for any scripted close, so
   * recording here means there is one path rather than a click handler that has to be kept in step
   * with the form's own behaviour.
   */
  // The theme's own mechanism, not a second one: lockScroll sets `scroll-lock` on <html>, which
  // assets/base.css already styles. showModal() makes the background inert but does not stop it
  // scrolling, so without this the shop slides about behind the gate.
  if (dialog.open) lockScroll(dialog);

  dialog.addEventListener('close', () => {
    unlockScroll(dialog);
    try {
      window.localStorage.setItem(KEY, String(Date.now()));
    } catch (err) {
      // Private mode, or storage blocked by the visitor. Nothing to do: they will be asked again,
      // which is the safe direction to fail in for this particular control.
    }
  });

  /**
   * `cancel` is what Escape fires. Preventing it holds the gate shut, matching the live storefront
   * (`closeByKey: false`).
   *
   * This is not a keyboard trap under WCAG 2.1.2, which asks that focus can be moved away using
   * the keyboard: the Enter button is in the tab order and answering the question releases the
   * dialog. What Escape must not be is a way past the question without answering it.
   */
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
  });
}
